import { PayloadAction } from "@reduxjs/toolkit";
import { call, put, takeLatest } from "redux-saga/effects";
import api from "service/apiService";
import { translate } from "utils";
import {
	CreateCampaignPayload,
	CreateCampaignRulePayload,
	CreateCampaignUserPayload,
	DeleteCampaignPayload,
	DeleteRuleCampaignPayload,
	GetDetailCampaignPayload,
	GetListCampaignPayload,
	GetListRuleCampaignPayload,
	GetListUserCampaignPayload,
	UpdateCampaignPayload,
	UpdateStatusCampaignPayload,
	createCampaignAsync,
	createCampaignRuleAsync,
	createCampaignUserAsync,
	deleteCampaignAsync,
	deleteRuleCampaignAsync,
	getDetailCampaignAsync,
	getListCampaignAsync,
	getListRuleCampaignAsync,
	getListUserCampaignAsync,
	setCampaignDetail,
	setCampaignList,
	updateCampaignAsync,
	updateStatusCampaignAsync,
} from "./campaign.slice";
import { message } from "antd";
import { setCampaignFilter } from "store/filter/index.slice";

function* getListCampaignSaga(action: PayloadAction<GetListCampaignPayload>) {
	const { query, callback, cancelLoading } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const response = await api.get("/dashboard/campaigns", {
				...query,
				skip: query.skip * query.take,
			});
			return { response };
		} catch (error) {
			return { error }
		}
	});
	if (response) {
		if(response.data.data.length === 0 && query.skip > 0) {
			const newQuery = { ...query, skip: query.skip - 1 }
			yield put({ type: setCampaignFilter.type, payload: newQuery });
			yield put({ type: getListCampaignAsync.type, payload: { query: newQuery, cancelLoading } });
		} else {
			yield put({ type: setCampaignList.type, payload: response.data });
		}
		callback?.();
		cancelLoading?.();
	} else {
		message.error(translate(error.message));
	}
}

function* watchGetListCampaign() {
	yield takeLatest(getListCampaignAsync, getListCampaignSaga);
}

function* getDetailCampaignSaga(
	action: PayloadAction<GetDetailCampaignPayload>,
) {
	const { id, callback } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const response = await api.get(`/dashboard/campaigns/${id}`);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		callback?.(response?.data?.data);
		yield put({ type: setCampaignDetail.type, payload: response.data });
	} else {
		message.error(translate(error.message));
	}
}

function* watchGetDetailCampaign() {
	yield takeLatest(getDetailCampaignAsync, getDetailCampaignSaga);
}

function* createCampaignSaga(action: PayloadAction<CreateCampaignPayload>) {
	const { data, callback } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const response = await api.post("/dashboard/campaigns", data);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		callback?.(response.data.data);
		yield put({ type: setCampaignDetail.type, payload: response.data });
	} else {
		message.error(translate(error.message));
	}
}

function* watchCreateCampaign() {
	yield takeLatest(createCampaignAsync, createCampaignSaga);
}

function* createCampaignRuleSaga(
	action: PayloadAction<CreateCampaignRulePayload>,
) {
	const { data, callback } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const response = await api.post(
				"/dashboard/campaigns/campaign-rules",
				data,
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		callback?.();
	} else {
		message.error(translate(error.message));
	}
}

function* watchCreateRuleCampaign() {
	yield takeLatest(createCampaignRuleAsync, createCampaignRuleSaga);
}

function* createCampaignUserSaga(
	action: PayloadAction<CreateCampaignUserPayload>,
) {
	const { data, callback } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const response = await api.post(
				"/dashboard/campaigns/campaign-users",
				data,
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		callback?.();
	} else {
		message.error(translate(error.message));
	}
}

function* watchCreateUserCampaign() {
	yield takeLatest(createCampaignUserAsync, createCampaignUserSaga);
}

function* updateCampaignSaga(action: PayloadAction<UpdateCampaignPayload>) {
	const { id, data, callback } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const response = await api.patch(
				`/dashboard/campaigns/${id}`,
				data,
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		callback?.();
		yield put({ type: setCampaignDetail.type, payload: response.data });
	} else {
		message.error(translate(error.message));
	}
}

function* watchUpdateCampaign() {
	yield takeLatest(updateCampaignAsync, updateCampaignSaga);
}

function* updateStatusCampaignSaga(
	action: PayloadAction<UpdateStatusCampaignPayload>,
) {
	const { id, data, callback } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const response = await api.patch(
				`/dashboard/campaigns/${id}`,
				data,
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		callback?.();
	} else {
		message.error(translate(error.message));
	}
}


function* watchUpdateStatusCampaign() {
	yield takeLatest(updateStatusCampaignAsync, updateStatusCampaignSaga);
}


function* deleteCampaignSaga(action: PayloadAction<DeleteCampaignPayload>) {
	const { id, callback } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const response = await api.delete(`/dashboard/campaigns/${id}`);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		callback?.();
		message.success(translate("Delete successful"));
	} else {
		message.error(translate(error.message));
	}
}

function* watchDeleteCampaign() {
	yield takeLatest(deleteCampaignAsync, deleteCampaignSaga);
}

function* getListCampaignUserSaga(
	action: PayloadAction<GetListUserCampaignPayload>,
) {
	const { id, query, callback } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const response = await api.get(`/dashboard/campaigns/${id}/users`, query);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		callback?.(response.data.data);
	} else {
		message.error(translate(error.message));
	}
}

function* watchGetListCampaignUserSaga() {
	yield takeLatest(getListUserCampaignAsync, getListCampaignUserSaga);
}


function* getListCampaignRuleSaga(
	action: PayloadAction<GetListRuleCampaignPayload>,
) {
	const { id, query, callback } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const response = await api.get(`/dashboard/campaigns/${id}/rules`, query);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		callback?.(response.data.data);
	} else {
		message.error(translate(error.message));
	}
}

function* watchGetListCampaignRuleSaga() {
	yield takeLatest(getListRuleCampaignAsync, getListCampaignRuleSaga);
}

function* deleteRuleCampaignSaga(action: PayloadAction<DeleteRuleCampaignPayload>) {
	const { id, callback } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const response = await api.delete(`/dashboard/campaigns/campaign-rules/${id}`);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		callback?.();
		message.success(translate("Delete successful"));
	} else {
		message.error(translate(error.message));
	}
}

function* watchDeleteRuleCampaign() {
	yield takeLatest(deleteRuleCampaignAsync, deleteRuleCampaignSaga);
}

const campaignSaga = [
	watchGetListCampaign(),
	watchUpdateCampaign(),
	watchGetDetailCampaign(),
	watchCreateCampaign(),
	watchCreateRuleCampaign(),
	watchCreateUserCampaign(),
	watchUpdateStatusCampaign(),
	watchDeleteCampaign(),
	watchDeleteRuleCampaign(),
	watchGetListCampaignUserSaga(),
	watchGetListCampaignRuleSaga(),
];

export default campaignSaga;
